import * as ents from '@attain-apps/entities'
import { TypeChecker } from '@attain-apps/type-guards'

import type * as models from '../../models'

import type { Feature } from './feature'

const CONFIRMATION_DIALOG_TITLE = 'Confirmation Dialog'
const CONFIRMATION_DIALOG_MSG = 'Are you sure?'

export class Selectors {
  /** TODO:  */
  private readonly _ongoingActionExecutionLookup
  /** TODO:  */
  public readonly hasOngoingActionExecutions
  /** TODO:  */
  private readonly _handlerExecutionContext
  /** TODO:  */
  public readonly actionIsExecuting

  /**
   * Enables the confirmation workflow when {@link ents.core.mosaic.Action.Ref}
   * requires approval.
   *
   * @see {@link ents.core.mosaic.Action.Ref.confirm}
   */
  public readonly confirmationDialog
  /**
   * Confirmation dialog title selector
   *
   * If confirmation dialogue is boolean the default message is shown __NOTE:__
   * If {@link ents.core.mosaic.Action.Confirmation.confirm} is a `true`, this
   * message defaults to {@link CONFIRMATION_DIALOG_MSG}.
   *
   * @default undefined
   */
  public readonly confirmationDialogTitle
  /**
   * Confirmation dialog message selector
   *
   * If confirmation dialogue is boolean the default message is shown
   * __NOTE:__ If {@link ents.core.mosaic.Action.Confirmation.confirm} is a
   * `true`, * this message defaults to {@link CONFIRMATION_DIALOG_TITLE}.
   *
   * @default undefined
   */
  public readonly confirmationDialogMessage
  /**
   * Confirmation request action
   *
   * Used for tracking submitting and cancelling confirmation
   */
  public readonly confirmationRequest
  /** Confirmation data selector used to achieve improved caching by catching any confirmationData change */
  private readonly _confirmationData
  /** Action ref selector used to achieve improved caching by catching any actionRef change */
  private readonly _confirmationActionRef

  constructor(feature: Feature) {
    this._ongoingActionExecutionLookup = feature.state.project(
      state => state.ongoingActionExecutionLookup,
    )
    this.hasOngoingActionExecutions =
      this._ongoingActionExecutionLookup.project(
        lookup => Object.values(lookup).length > 0,
      )
    this._handlerExecutionContext = feature.createSelectorMethod(
      ({
        handlerId,
        handler,
      }: ExactlyOneOf<{
        handlerId: models.action.Handler.Id
        handler: models.action.Handler
      }>) =>
        this._ongoingActionExecutionLookup.project(
          lookup => lookup[handler ? handler.context.id : handlerId],
        ),
    )

    this.actionIsExecuting = feature.createSelectorMethod(
      (
        params: ExactlyOneOf<{
          handlerId: models.action.Handler.Id
          handler: models.action.Handler
        }>,
      ) =>
        this._handlerExecutionContext(params).project(context => !!context)
          .asSelector,
    )
    this.confirmationRequest = feature.state.project(
      state => state.confirmationRequestAction,
    )

    this._confirmationData = this.confirmationRequest.project(
      confirmationRequest => confirmationRequest?.data,
    )
    this._confirmationActionRef = this._confirmationData.project(
      confirmationData => confirmationData?.context.actionRef,
    )

    this.confirmationDialog = this._confirmationActionRef.project(
      confirmationActionRef => confirmationActionRef?.confirm,
    )

    this.confirmationDialogTitle = this.confirmationDialog.project(
      confirmationDialog =>
        TypeChecker.isNotNullish(confirmationDialog) &&
        !TypeChecker.isBoolean(confirmationDialog) &&
        confirmationDialog.title
          ? confirmationDialog.title
          : CONFIRMATION_DIALOG_TITLE,
    )

    this.confirmationDialogMessage = this.confirmationDialog.project(
      confirmationDialog =>
        TypeChecker.isNotNullish(confirmationDialog) &&
        !TypeChecker.isBoolean(confirmationDialog) &&
        confirmationDialog.message
          ? confirmationDialog.message
          : CONFIRMATION_DIALOG_MSG,
    )
  }
}
